import { Panel } from '@playcanvas/pcui/react';
import React, { useState } from 'react';

import { SetProperty, AircraftConfigData, AircraftConfiguration, AnimationBinding } from '../../types';

interface AircraftConfigPanelProps {
    aircraftConfigData: AircraftConfigData;
    setProperty: SetProperty;
}

// Helper functions to try both naming conventions
const getString = (config: AircraftConfiguration, camelCase: string, pascalCase: string): string => {
    return config[pascalCase] || config[camelCase] || '';
};

const getNumber = (config: AircraftConfiguration, camelCase: string, pascalCase: string): number => {
    return config[pascalCase] ?? config[camelCase] ?? 0;
};

const getBoolean = (config: AircraftConfiguration, camelCase: string, pascalCase: string): boolean => {
    return config[pascalCase] ?? config[camelCase] ?? false;
};

const getArray = (config: AircraftConfiguration, camelCase: string, pascalCase: string): any[] => {
    return config[pascalCase] || config[camelCase] || [];
};

const formatSpeed = (speed: number): string => {
    return speed > 0 ? `${speed} kts` : 'N/A';
};

const formatWeight = (weight: number): string => {
    return weight > 0 ? `${weight.toLocaleString()} lbs` : 'N/A';
};

const formatDistance = (distance: number): string => {
    return distance !== 0 ? `${distance} ft` : 'N/A';
};

const getSoundSetName = (soundSet: number): string => {
    const soundSetMap: Record<number, string> = {
        0: 'Single Engine Piston',
        1: 'Warbird',
        2: 'Passenger Jet',
        3: 'Fighter Jet',
        4: 'Turbo Prop',
        5: 'Custom',
        6: 'Twin Turbo Prop',
        7: 'Military Quad Turbo Prop',
        8: 'TBM930',
        9: 'A10',
        10: 'Boeing 777',
        11: 'Boeing 757'
    };
    return soundSetMap[soundSet] || `Sound Set ${soundSet}`;
};

const getWeightPointTypeName = (type: string | number): string => {
    // Handle C# enum WeightPointType (can be string or numeric)
    const typeMap: Record<string | number, string> = {
        // String values
        'Structure': 'Structure',
        'FuelTank': 'Fuel Tank',
        'Passenger': 'Passenger',
        'Cargo': 'Cargo',
        'GenericUserConfigurable': 'User Configurable',
        // Numeric values (enum indices)
        0: 'Structure',
        1: 'Fuel Tank',
        2: 'Passenger',
        3: 'Cargo',
        4: 'User Configurable'
    };
    return typeMap[type] || type?.toString() || 'Unknown';
};

const getReverseThrustName = (restrictions: string | number): string => {
    // Handle C# enum ReverseThrustRestrictions (can be string or numeric)
    const restrictionsMap: Record<string | number, string> = {
        // String values
        'GroundAndSpeed': 'Ground & Speed Limited',
        'Unrestricted': 'Unrestricted',
        'Disabled': 'Disabled',
        // Numeric values (enum indices)
        0: 'Ground & Speed Limited',
        1: 'Unrestricted',
        2: 'Disabled'
    };
    return restrictionsMap[restrictions] || restrictions?.toString() || 'Unknown';
};

// Collapsible section component
const CollapsibleSection: React.FC<{
    title: string;
    count?: number;
    defaultCollapsed?: boolean;
    children: React.ReactNode;
}> = ({ title, count, defaultCollapsed = false, children }) => {
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
    
    const displayTitle = count !== undefined ? `${title} (${count})` : title;
    
    return (
        <div className="aircraft-config-collapsible-section">
            <div 
                className="aircraft-config-subsection-header clickable"
                onClick={() => setIsCollapsed(!isCollapsed)}
                style={{ cursor: 'pointer', userSelect: 'none' }}
            >
                <span style={{ marginRight: '8px' }}>
                    {isCollapsed ? '▶' : '▼'}
                </span>
                {displayTitle}
            </div>
            {!isCollapsed && (
                <div className="aircraft-config-collapsible-content">
                    {children}
                </div>
            )}
        </div>
    );
};

const BasicInfoSection = ({ config }: { config: AircraftConfiguration }) => {
    // Debug: log the actual config structure
    console.log('Aircraft config structure:', Object.keys(config));
    
    return (
        <div className="aircraft-config-section">
            <h4>Basic Information</h4>
            <div className="aircraft-config-item">
                <span className="label">Full Name:</span>
                <span className="value">{getString(config, 'fullName', 'FullName') || 'Unknown'}</span>
            </div>
            <div className="aircraft-config-item">
                <span className="label">Sound Set:</span>
                <span className="value">{getSoundSetName(getNumber(config, 'soundSet', 'SoundSet'))}</span>
            </div>
            <div className="aircraft-config-item">
                <span className="label">Bounding Radius:</span>
                <span className="value">{getNumber(config, 'boundingRadius', 'BoundingRadius')?.toFixed(1) || 'N/A'} m</span>
            </div>
        </div>
    );
};

const CapabilitiesSection = ({ config }: { config: AircraftConfiguration }) => (
    <div className="aircraft-config-section">
        <h4>Aircraft Capabilities</h4>
        <div className="aircraft-config-item">
            <span className="label">Retractable Gear:</span>
            <span className="value">{getBoolean(config, 'hasRetractibleGear', 'HasRetractibleGear') ? 'Yes' : 'No'}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">Virtual Cockpit:</span>
            <span className="value">{getBoolean(config, 'hasVirtualCockpit', 'HasVirtualCockpit') ? 'Yes' : 'No'}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">Autopilot:</span>
            <span className="value">{getBoolean(config, 'hasAutopilot', 'HasAutopilot') ? 'Yes' : 'No'}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">Arresting Hook:</span>
            <span className="value">{getBoolean(config, 'hasArrestingHook', 'HasArrestingHook') ? 'Yes' : 'No'}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">Stabilator:</span>
            <span className="value">{getBoolean(config, 'hasStabilator', 'HasStabilator') ? 'Yes' : 'No'}</span>
        </div>
    </div>
);

const WeightBalanceSection = ({ config }: { config: AircraftConfiguration }) => {
    const weightPoints = getArray(config, 'weightPointDefinitions', 'WeightPointDefinitions');

    return (
        <div className="aircraft-config-section">
            <h4>Weight & Balance</h4>
            <div className="aircraft-config-item">
                <span className="label">Max Takeoff Weight:</span>
                <span className="value">{formatWeight(getNumber(config, 'maxTakeoffWeight', 'MaxTakeoffWeight'))}</span>
            </div>
            <div className="aircraft-config-item">
                <span className="label">Max Landing Weight:</span>
                <span className="value">{formatWeight(getNumber(config, 'maxLandingWeight', 'MaxLandingWeight'))}</span>
            </div>
            
            {weightPoints.length > 0 && (
                <CollapsibleSection 
                    title="Weight Points" 
                    count={weightPoints.length}
                    defaultCollapsed={true}
                >
                    {weightPoints.map((point: any, index: number) => {
                        // Use actual C# property names from WeightPointDefinition
                        const name = point.Name || `Point ${index + 1}`;
                        const type = point.WeightPointType;
                        const mass = point.Mass; // current mass in kg
                        const maxMass = point.MaxMass; // max mass in kg
                        const minMass = point.MinMass; // min mass in kg
                        const isConfigurable = point.IsConfigurable;
                        const localPosition = point.LocalPosition;
                        const dimensions = point.Dimensions;
                        
                        return (
                            <div key={index} className="aircraft-config-weight-point">
                                <div className="aircraft-config-item">
                                    <span className="label">Name:</span>
                                    <span className="value">{name}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Type:</span>
                                    <span className="value">{getWeightPointTypeName(type)}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Current Mass:</span>
                                    <span className="value">{mass ? `${(mass * 2.20462).toFixed(0)} lbs (${mass.toFixed(0)} kg)` : 'N/A'}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Max Mass:</span>
                                    <span className="value">{maxMass ? `${(maxMass * 2.20462).toFixed(0)} lbs (${maxMass.toFixed(0)} kg)` : 'N/A'}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Min Mass:</span>
                                    <span className="value">{minMass ? `${(minMass * 2.20462).toFixed(0)} lbs (${minMass.toFixed(0)} kg)` : 'N/A'}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Configurable:</span>
                                    <span className="value">{isConfigurable ? 'Yes' : 'No'}</span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Local Position:</span>
                                    <span className="value">
                                        {localPosition ? 
                                            `X: ${localPosition.X?.toFixed(2) || 0}, Y: ${localPosition.Y?.toFixed(2) || 0}, Z: ${localPosition.Z?.toFixed(2) || 0}` : 
                                            'N/A'
                                        }
                                    </span>
                                </div>
                                <div className="aircraft-config-item">
                                    <span className="label">Dimensions:</span>
                                    <span className="value">
                                        {dimensions ? 
                                            `X: ${dimensions.X?.toFixed(2) || 0}, Y: ${dimensions.Y?.toFixed(2) || 0}, Z: ${dimensions.Z?.toFixed(2) || 0}` : 
                                            'N/A'
                                        }
                                    </span>
                                </div>
                                {index < weightPoints.length - 1 && <div className="aircraft-config-separator" />}
                            </div>
                        );
                    })}
                </CollapsibleSection>
            )}
        </div>
    );
};

const SpeedsSection = ({ config }: { config: AircraftConfiguration }) => (
    <div className="aircraft-config-section">
        <h4>Speed Limitations</h4>
        <div className="aircraft-config-item">
            <span className="label">VMO (Never Exceed):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vmo', 'VMO'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">MMO (Mach Limit):</span>
            <span className="value">{getNumber(config, 'mmo', 'MMO') > 0 ? `M${getNumber(config, 'mmo', 'MMO')}` : 'N/A'}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VS (Clean Stall):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vs', 'VS'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VS0 (Full Flaps Stall):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vS0', 'VS0'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VNO (Normal Operating):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vno', 'VNO'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VFE (Flaps Extended):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vfe', 'VFE'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">Best Glide:</span>
            <span className="value">{formatSpeed(getNumber(config, 'bestGlide', 'BestGlide'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VX (Best Angle of Climb):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vx', 'VX'))}</span>
        </div>
        <div className="aircraft-config-item">
            <span className="label">VY (Best Rate of Climb):</span>
            <span className="value">{formatSpeed(getNumber(config, 'vy', 'VY'))}</span>
        </div>
    </div>
);

const EnginesSection = ({ config }: { config: AircraftConfiguration }) => {
    const engines = getArray(config, 'engineDefinitions', 'EngineDefinitions');
    
    if (engines.length === 0) {
        return (
            <div className="aircraft-config-section">
                <h4>Engines</h4>
                <div className="aircraft-config-item">
                    <span className="value">No engine information available</span>
                </div>
            </div>
        );
    }

    return (
        <div className="aircraft-config-section">
            <h4>Engines ({engines.length})</h4>
            {engines.map((engine: any, index: number) => {
                // Use actual C# property names from EngineDefinition
                const engineTypeName = engine.EngineTypeName;
                const position = engine.Position;
                const maxThrust = engine.MaxThrust; // in kgf
                const maxThrustFuelBurn = engine.MaxThrustFuelBurn; // in Kg/h
                const maxRPM = engine.MaxRPM;
                const staticMaxRPM = engine.StaticMaxRPM;
                const startupDuration = engine.StartupDuration;
                const reverseThrustRestrictions = engine.ReverseThrustRestrictions;
                const useAnimations = engine.UseAnimations;
                const animationName = engine.AnimationName;
                const animationNames = engine.AnimationNames;
                
                // Convert kgf to lbf (1 kgf = 2.20462 lbf)
                const maxThrustLbf = maxThrust ? (maxThrust * 2.20462).toFixed(0) : null;
                
                return (
                    <div key={index} className="aircraft-config-engine">
                        <div className="aircraft-config-subsection-header">
                            Engine {index + 1}
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Type:</span>
                            <span className="value">{engineTypeName || 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Max Thrust:</span>
                            <span className="value">{maxThrustLbf ? `${maxThrustLbf} lbf (${maxThrust} kgf)` : 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Fuel Burn (90% Power):</span>
                            <span className="value">{maxThrustFuelBurn ? `${maxThrustFuelBurn} kg/h` : 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Max RPM:</span>
                            <span className="value">{maxRPM || 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Static Max RPM:</span>
                            <span className="value">{staticMaxRPM || 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Startup Duration:</span>
                            <span className="value">{startupDuration ? `${startupDuration}s` : 'N/A'}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Position:</span>
                            <span className="value">
                                {position ? 
                                    `X: ${position.X?.toFixed(2) || 0}, Y: ${position.Y?.toFixed(2) || 0}, Z: ${position.Z?.toFixed(2) || 0}` : 
                                    'N/A'
                                }
                            </span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Reverse Thrust:</span>
                            <span className="value">{getReverseThrustName(reverseThrustRestrictions)}</span>
                        </div>
                        <div className="aircraft-config-item">
                            <span className="label">Uses Animations:</span>
                            <span className="value">{useAnimations ? 'Yes' : 'No'}</span>
                        </div>
                        {(animationNames && animationNames.length > 0) ? (
                            <div className="aircraft-config-item">
                                <span className="label">Animation Names:</span>
                                <span className="value">{animationNames.join(', ')}</span>
                            </div>
                        ) : animationName && (
                            <div className="aircraft-config-item">
                                <span className="label">Animation Name:</span>
                                <span className="value">{animationName} (deprecated)</span>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const AnimationBindingsList = ({ title, bindings }: { title: string; bindings: AnimationBinding[] }) => {
    if (!bindings || bindings.length === 0) return null;

    const formatArray = (arr?: number[]) => (arr && arr.length ? arr.join(', ') : '');

    return (
        <CollapsibleSection title={title} count={bindings.length} defaultCollapsed={true}>
            {bindings.map((b: AnimationBinding, i: number) => (
                <div key={i} className="aircraft-config-item">
                    <div className="aircraft-config-item"><span className="label">Name:</span><span className="value">{b.Name || 'N/A'}</span></div>
                    {b.AnimationName && (
                        <div className="aircraft-config-item"><span className="label">Animation:</span><span className="value">{b.AnimationName}</span></div>
                    )}
                    {(b.Input && b.Input.length) ? (
                        <div className="aircraft-config-item"><span className="label">Input:</span><span className="value">{formatArray(b.Input)}</span></div>
                    ) : null}
                    {(b.Animation && b.Animation.length) ? (
                        <div className="aircraft-config-item"><span className="label">Values:</span><span className="value">{formatArray(b.Animation)}</span></div>
                    ) : null}
                    {i < bindings.length - 1 && <div className="aircraft-config-separator" />}
                </div>
            ))}
        </CollapsibleSection>
    );
};

const CockpitUIItemsSection = ({ config }: { config: AircraftConfiguration }) => {
    const cockpitUIItems = getArray(config, 'cockpitUIItems', 'CockpitUIItems');
    
    if (cockpitUIItems.length === 0) {
        return null;
    }

    const formatArrayValue = (arr: any[]): string => {
        if (!arr || arr.length === 0) return 'N/A';
        return arr.join(', ');
    };

    const formatNumberValue = (value: number): string => {
        return value !== undefined && value !== null ? value.toString() : 'N/A';
    };

    return (
        <div className="aircraft-config-section">
            <h4>Cockpit UI</h4>
            <CollapsibleSection 
                title="UI Items" 
                count={cockpitUIItems.length}
                defaultCollapsed={true}
            >
                {cockpitUIItems.map((item: any, index: number) => {
                    const name = item.Name || `UI Item ${index + 1}`;
                    const uiName = item.UIName;
                    const bindingName = item.BindingName;
                    const bindingType = item.BindingType;
                    
                    // Create a display title similar to C# ToString() method
                    let displayTitle = name;
                    if (uiName && name !== uiName) {
                        displayTitle = `${name} (${uiName})`;
                    } else if (!name && uiName) {
                        displayTitle = uiName;
                    }
                    if (bindingName || bindingType) {
                        displayTitle += ` - ${bindingName || 'Unknown'} (${bindingType || 'Unknown'})`;
                    }
                    
                    return (
                        <div key={index} className="aircraft-config-cockpit-ui-item">
                            <div className="aircraft-config-item" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
                                <span className="value">{displayTitle}</span>
                            </div>
                            
                            {/* Basic Properties */}
                            {item.Name && (
                                <div className="aircraft-config-item">
                                    <span className="label">Name:</span>
                                    <span className="value">{item.Name}</span>
                                </div>
                            )}
                            
                            {/* Node Properties */}
                            {(item.NodeNames && item.NodeNames.length > 0) ? (
                                <div className="aircraft-config-item">
                                    <span className="label">Node Names:</span>
                                    <span className="value">{formatArrayValue(item.NodeNames)}</span>
                                </div>
                            ) : item.NodeName && (
                                <div className="aircraft-config-item">
                                    <span className="label">Node Name:</span>
                                    <span className="value">{item.NodeName} (deprecated)</span>
                                </div>
                            )}
                            
                            {/* UI Properties */}
                            {item.UIName && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Name:</span>
                                    <span className="value">{item.UIName}</span>
                                </div>
                            )}
                            {item.UIType && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Type:</span>
                                    <span className="value">{item.UIType}</span>
                                </div>
                            )}
                            {item.UIGroup && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Group:</span>
                                    <span className="value">{item.UIGroup}</span>
                                </div>
                            )}
                            {item.Group && (
                                <div className="aircraft-config-item">
                                    <span className="label">Group:</span>
                                    <span className="value">{item.Group}</span>
                                </div>
                            )}
                            
                            {/* Binding Properties */}
                            {item.BindingType && (
                                <div className="aircraft-config-item">
                                    <span className="label">Binding Type:</span>
                                    <span className="value">{item.BindingType}</span>
                                </div>
                            )}
                            {item.BindingName && (
                                <div className="aircraft-config-item">
                                    <span className="label">Binding Name:</span>
                                    <span className="value">{item.BindingName}</span>
                                </div>
                            )}
                            
                            {/* Value Range Properties */}
                            {(item.MinValue !== undefined && item.MinValue !== null) && (
                                <div className="aircraft-config-item">
                                    <span className="label">Min Value:</span>
                                    <span className="value">{formatNumberValue(item.MinValue)}</span>
                                </div>
                            )}
                            {(item.MaxValue !== undefined && item.MaxValue !== null) && (
                                <div className="aircraft-config-item">
                                    <span className="label">Max Value:</span>
                                    <span className="value">{formatNumberValue(item.MaxValue)}</span>
                                </div>
                            )}
                            
                            {/* Operation Properties */}
                            {item.Operation && item.Operation !== 'None' && (
                                <div className="aircraft-config-item">
                                    <span className="label">Operation:</span>
                                    <span className="value">{item.Operation}</span>
                                </div>
                            )}
                            {(item.OperationValue !== undefined && item.OperationValue !== null) && (
                                <div className="aircraft-config-item">
                                    <span className="label">Operation Value:</span>
                                    <span className="value">{formatNumberValue(item.OperationValue)}</span>
                                </div>
                            )}
                            
                            {/* Array Properties */}
                            {item.UIValues && item.UIValues.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Values:</span>
                                    <span className="value">{formatArrayValue(item.UIValues)}</span>
                                </div>
                            )}
                            {item.UIFlyoutValues && item.UIFlyoutValues.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Flyout Values:</span>
                                    <span className="value">{formatArrayValue(item.UIFlyoutValues)}</span>
                                </div>
                            )}
                            {item.UIShortValues && item.UIShortValues.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">UI Short Values:</span>
                                    <span className="value">{formatArrayValue(item.UIShortValues)}</span>
                                </div>
                            )}
                            {item.InputValues && item.InputValues.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">Input Values:</span>
                                    <span className="value">{formatArrayValue(item.InputValues)}</span>
                                </div>
                            )}
                            {item.AnimValues && item.AnimValues.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">Animation Values:</span>
                                    <span className="value">{formatArrayValue(item.AnimValues)}</span>
                                </div>
                            )}
                            {item.MaterialNames && item.MaterialNames.length > 0 && (
                                <div className="aircraft-config-item">
                                    <span className="label">Material Names:</span>
                                    <span className="value">{formatArrayValue(item.MaterialNames)}</span>
                                </div>
                            )}
                            
                            {/* Boolean Properties */}
                            {item.Interpolate !== undefined && (
                                <div className="aircraft-config-item">
                                    <span className="label">Interpolate:</span>
                                    <span className="value">{item.Interpolate ? 'Yes' : 'No'}</span>
                                </div>
                            )}
                            {item.IsReel !== undefined && (
                                <div className="aircraft-config-item">
                                    <span className="label">Is Reel:</span>
                                    <span className="value">{item.IsReel ? 'Yes' : 'No'}</span>
                                </div>
                            )}
                            
                            {/* Additional Properties */}
                            {(item.InterpolationSpeed !== undefined && item.InterpolationSpeed !== null) && (
                                <div className="aircraft-config-item">
                                    <span className="label">Interpolation Speed:</span>
                                    <span className="value">{formatNumberValue(item.InterpolationSpeed)}</span>
                                </div>
                            )}
                            {item.DisplayUnits && item.DisplayUnits !== 'Direct' && (
                                <div className="aircraft-config-item">
                                    <span className="label">Display Units:</span>
                                    <span className="value">{item.DisplayUnits}</span>
                                </div>
                            )}
                            {(item.SystemIndex !== undefined && item.SystemIndex !== null) && (
                                <div className="aircraft-config-item">
                                    <span className="label">System Index:</span>
                                    <span className="value">{formatNumberValue(item.SystemIndex)}</span>
                                </div>
                            )}
                            {item.AnimationName && (
                                <div className="aircraft-config-item">
                                    <span className="label">Animation Name:</span>
                                    <span className="value">{item.AnimationName}</span>
                                </div>
                            )}
                            
                            {index < cockpitUIItems.length - 1 && <div className="aircraft-config-separator" />}
                        </div>
                    );
                })}
            </CollapsibleSection>
        </div>
    );
};

class AircraftConfigPanel extends React.Component<AircraftConfigPanelProps> {
    shouldComponentUpdate(nextProps: Readonly<AircraftConfigPanelProps>): boolean {
        return (
            JSON.stringify(nextProps.aircraftConfigData) !== JSON.stringify(this.props.aircraftConfigData)
        );
    }

    render() {
        const { aircraftConfigData } = this.props;
        
        if (!aircraftConfigData.loaded) {
            return (
                <Panel 
                    headerText='AIRCRAFT CONFIGURATION' 
                    class='aircraft-config-panel' 
                    enabled={false}
                    collapsible={true}
                    collapsed={true}
                >
                    <div className="aircraft-config-loading">
                        No aircraft configuration file loaded
                    </div>
                </Panel>
            );
        }

        if (aircraftConfigData.error) {
            return (
                <Panel 
                    headerText='AIRCRAFT CONFIGURATION' 
                    class='aircraft-config-panel' 
                    enabled={true}
                    collapsible={true}
                    collapsed={true}
                >
                    <div className="aircraft-config-error">
                        Error loading configuration: {aircraftConfigData.error}
                    </div>
                </Panel>
            );
        }

        if (!aircraftConfigData.configuration) {
            return (
                <Panel 
                    headerText='AIRCRAFT CONFIGURATION' 
                    class='aircraft-config-panel' 
                    enabled={false}
                    collapsible={true}
                    collapsed={true}
                >
                    <div className="aircraft-config-loading">
                        No configuration data available
                    </div>
                </Panel>
            );
        }

        const config = aircraftConfigData.configuration;

        return (
            <Panel 
                headerText='AIRCRAFT CONFIGURATION' 
                class='aircraft-config-panel' 
                enabled={true}
                collapsible={true}
                collapsed={true}
            >
                <div className="aircraft-config-content">
                    <BasicInfoSection config={config} />
                    <CapabilitiesSection config={config} />
                    <WeightBalanceSection config={config} />
                    <SpeedsSection config={config} />
                    <EnginesSection config={config} />
                    <AnimationBindingsList 
                        title="Variant Animation Bindings" 
                        bindings={(config.VariantsAnimationBindings || config.variantsAnimationBindings) as AnimationBinding[]}
                    />
                    <AnimationBindingsList 
                        title="Removable Objects Animation Bindings" 
                        bindings={(config.RemovableObjectsAnimationBindings || config.removableObjectsAnimationBindings) as AnimationBinding[]}
                    />
                    <CockpitUIItemsSection config={config} />
                </div>
            </Panel>
        );
    }
}

export default AircraftConfigPanel; 